import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { surfaceSx } from "../center/ownerCenterUi";
import { OwnerInitiativeDialog } from "../center/OwnerQuickDialogs";

const STATUS_LABELS = { active: "Activo", paused: "Pausado", done: "Terminado", archived: "Archivado" };
const PRIORITY_LABELS = { low: "Baja", normal: "Normal", high: "Alta" };

function idOf(value) { return String(value?.id || value?._id || value || ""); }
function minutesLabel(value) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export default function OwnerClientProjectsPanel({ client, context, loading = false, error = "", onRefresh }) {
  const initiatives = context?.initiatives || [];
  const [createOpen, setCreateOpen] = React.useState(false);
  const navigate = useNavigate();

  return (
    <>
      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Stack direction="row" spacing={.6} alignItems="center"><WorkspacesRoundedIcon sx={{ fontSize: 18 }} /><Typography sx={{ fontWeight: 1000 }}>Proyectos</Typography></Stack>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>La ficha coordina objetivo, próximos pasos, venta, horas, contrato y Project WebProfit sin duplicar esas fuentes.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)} sx={{ textTransform: "none", fontWeight: 950 }}>Nuevo proyecto</Button>
        </Stack>
        <Divider sx={{ my: 1.2 }} />
        {!context && loading ? <Box sx={{ py: 2.2, display: "flex", justifyContent: "center" }}><CircularProgress size={22} /></Box> : null}
        {!context && error ? <Alert severity="error" action={<Button size="small" onClick={onRefresh} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}
        {context && initiatives.length ? (
          <Stack divider={<Divider flexItem />}>
            {initiatives.map((row) => (
              <Box
                key={idOf(row)}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/dashboard/owner/proyectos/${idOf(row)}`)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate(`/dashboard/owner/proyectos/${idOf(row)}`); } }}
                sx={{ py: 1, px: .4, cursor: "pointer", borderRadius: 1.5, "&:hover": { bgcolor: "action.hover" } }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.8} alignItems={{ sm: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap alignItems="center">
                      <Typography sx={{ fontSize: ".86rem", fontWeight: 950 }}>{row.name}</Typography>
                      <Chip size="small" label={STATUS_LABELS[row.status] || "Activo"} sx={{ height: 20, fontSize: ".61rem", fontWeight: 850 }} />
                      {row.priority === "high" ? <Chip size="small" color="warning" label="Prioridad alta" sx={{ height: 20, fontSize: ".61rem", fontWeight: 850 }} /> : null}
                    </Stack>
                    <Typography sx={{ mt: .3, color: "text.secondary", fontSize: ".7rem" }}>{row.nextStep || row.outcome || "Sin próximo paso resumido"}</Typography>
                  </Box>
                  <Stack direction="row" spacing={.6} alignItems="center" sx={{ flex: "0 0 auto" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 850 }}>{row.estimatedMinutes ? `${minutesLabel(row.estimatedMinutes)} est.` : "Sin estimar"} · {Number(row.actualMinutes || 0) ? `${minutesLabel(row.actualMinutes)} reales` : "sin horas"} · {PRIORITY_LABELS[row.priority] || "Normal"}</Typography>
                    <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={(event) => { event.stopPropagation(); navigate(`/dashboard/owner/proyectos/${idOf(row)}`); }} sx={{ textTransform: "none", fontWeight: 900 }}>Abrir</Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : context ? <Typography sx={{ py: 1.2, color: "text.secondary", fontSize: ".8rem" }}>Todavía no hay proyectos para esta persona. Podés crear uno sin necesidad de venta, contrato ni Project WebProfit.</Typography> : null}
      </Box>

      <OwnerInitiativeDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={async () => { setCreateOpen(false); await onRefresh?.(); }} people={client ? [client] : []} preferredPerson={client} />
    </>
  );
}
